import { z } from 'zod';
import { toneSchema } from '@/components/block-primitives';
import { parseFence } from '@/components/block-primitives/block-fence-registry';

// A chart height declared on the data-view fence config so it round-trips through markdown. It
// must be a declared schema field, not just tolerated, because editing a block re-serializes from
// the zod-parsed config — an undeclared key would be stripped on the first inline edit.
export const CHART_MIN_HEIGHT = 220;
export const CHART_MAX_HEIGHT = 640;
export const DEFAULT_CHART_HEIGHT = 240;

export const clampChartHeight = (height: number): number =>
  Math.round(Math.min(CHART_MAX_HEIGHT, Math.max(CHART_MIN_HEIGHT, height)));

const dataViewModeSchema = z.enum(['line', 'bar', 'table']);

const dataValueSchema = z.union([
  z.number(),
  z.object({
    value: z.number(),
    lower: z.number().optional(),
    upper: z.number().optional(),
  }),
]);

const dataSeriesSchema = z.object({
  name: z.string(),
  values: z.array(dataValueSchema),
});

const dataViewSchema = z
  .object({
    title: z.string(),
    description: z.string().optional(),
    categories: z.array(z.string()).min(1),
    series: z.array(dataSeriesSchema).min(1),
    defaultView: dataViewModeSchema.optional(),
    format: z.enum(['decimal', 'percent', 'integer', 'currency']).optional(),
    // ISO 4217 code applied when format is 'currency'; defaults to USD when omitted.
    // Constrained to a well-formed 3-letter code: Intl.NumberFormat throws a RangeError on
    // anything else, and that throw would escape parseFence and crash the whole artifact view.
    currencyCode: z
      .string()
      .regex(/^[A-Za-z]{3}$/)
      .optional(),
    // BCP 47 locale tag driving number punctuation (e.g. 'de-DE' → 1.234 €); defaults to
    // en-US. A malformed tag makes Intl throw a RangeError, so the shape is validated here
    // and formatValue still guards defensively since it also runs inside render and d3.
    locale: z
      .string()
      .regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/)
      .optional(),
    yDomain: z
      .tuple([z.number(), z.number()])
      .refine(([min, max]) => min < max)
      .optional(),
    referenceLine: z.object({ label: z.string(), value: z.number() }).optional(),
    xLabel: z.string().optional(),
    yLabel: z.string().optional(),
    note: z.string().optional(),
    // Author-resized chart height in px; the block clamps and falls back to DEFAULT_CHART_HEIGHT.
    height: z.number().min(CHART_MIN_HEIGHT).max(CHART_MAX_HEIGHT).optional(),
  })
  // The chart aligns each series value to a category by index, so a ragged series would
  // plot against the wrong category.
  .refine(config => config.series.every(series => series.values.length === config.categories.length));

export type DataViewMode = z.infer<typeof dataViewModeSchema>;
export type DataValue = z.infer<typeof dataValueSchema>;
export type DataSeries = z.infer<typeof dataSeriesSchema>;
export type DataViewConfig = z.infer<typeof dataViewSchema>;

export const parseDataViewConfig = (raw: string): DataViewConfig | null => parseFence(raw, dataViewSchema);

export const getDataPoint = (value: DataValue) =>
  typeof value === 'number' ? { value } : { value: value.value, lower: value.lower, upper: value.upper };

// outcome is shown verbatim so the block can render any verdict Percy produces
// (agent judgments, upstream tool decisions), not just Footprint dispositions;
// severity carries the presentation tone independently.
const decisionSchema = z.object({
  title: z.string().optional(),
  outcome: z.string(),
  severity: toneSchema,
  headline: z.string().optional(),
  rationale: z.string().optional(),
  note: z.string().optional(),
});

export type DecisionBlockConfig = z.infer<typeof decisionSchema>;

export const parseDecisionBlockConfig = (raw: string): DecisionBlockConfig | null => parseFence(raw, decisionSchema);

const statItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  detail: z.string().optional(),
  tone: toneSchema.optional(),
});

const statSchema = z.object({
  title: z.string().optional(),
  stats: z.array(statItemSchema).min(1),
  note: z.string().optional(),
});

export type StatItem = z.infer<typeof statItemSchema>;
export type StatBlockConfig = z.infer<typeof statSchema>;

export const parseStatBlockConfig = (raw: string): StatBlockConfig | null => parseFence(raw, statSchema);

// body carries the aside; severity drives the tone independently, so a positive
// confirmation and a critical caveat share one block.
const calloutSchema = z.object({
  title: z.string().optional(),
  body: z.string(),
  severity: toneSchema,
  note: z.string().optional(),
});

export type CalloutBlockConfig = z.infer<typeof calloutSchema>;

export const parseCalloutBlockConfig = (raw: string): CalloutBlockConfig | null => parseFence(raw, calloutSchema);

const timelineEventSchema = z.object({
  label: z.string(),
  date: z.string().optional(),
  detail: z.string().optional(),
  tone: toneSchema.optional(),
});

const timelineSchema = z.object({
  title: z.string().optional(),
  events: z.array(timelineEventSchema).min(1),
  note: z.string().optional(),
});

export type TimelineEvent = z.infer<typeof timelineEventSchema>;
export type TimelineBlockConfig = z.infer<typeof timelineSchema>;

export const parseTimelineBlockConfig = (raw: string): TimelineBlockConfig | null => parseFence(raw, timelineSchema);

// state is a completion status, separate from tone: the renderer maps each state to
// its own icon and tone, so authors pick a status rather than styling directly.
const checklistStateSchema = z.enum(['complete', 'pending', 'blocked', 'na']);

const checklistItemSchema = z.object({
  label: z.string(),
  state: checklistStateSchema,
  detail: z.string().optional(),
});

const checklistSchema = z.object({
  title: z.string().optional(),
  items: z.array(checklistItemSchema).min(1),
  note: z.string().optional(),
});

export type ChecklistState = z.infer<typeof checklistStateSchema>;
export type ChecklistItem = z.infer<typeof checklistItemSchema>;
export type ChecklistBlockConfig = z.infer<typeof checklistSchema>;

export const parseChecklistBlockConfig = (raw: string): ChecklistBlockConfig | null => parseFence(raw, checklistSchema);

const recordFieldSchema = z.object({
  label: z.string(),
  value: z.string(),
  tone: toneSchema.optional(),
});

const recordSchema = z.object({
  // trim + min(1) so a blank name can't yield an empty monogram and empty aria-label.
  name: z.string().trim().min(1),
  subtitle: z.string().optional(),
  // short label for the identity monogram; falls back to the name's initials.
  monogram: z.string().optional(),
  badge: z.object({ label: z.string(), tone: toneSchema }).optional(),
  fields: z.array(recordFieldSchema).min(1),
  note: z.string().optional(),
});

export type RecordField = z.infer<typeof recordFieldSchema>;
export type RecordBlockConfig = z.infer<typeof recordSchema>;

export const parseRecordBlockConfig = (raw: string): RecordBlockConfig | null => parseFence(raw, recordSchema);

const funnelFormatSchema = z.enum(['integer', 'percent']);

const funnelStageSchema = z.object({
  label: z.string(),
  value: z.number(),
  tone: toneSchema.optional(),
  detail: z.string().optional(),
});

const funnelSchema = z.object({
  title: z.string().optional(),
  format: funnelFormatSchema.optional(),
  stages: z.array(funnelStageSchema).min(1),
  note: z.string().optional(),
});

export type FunnelFormat = z.infer<typeof funnelFormatSchema>;
export type FunnelStage = z.infer<typeof funnelStageSchema>;
export type FunnelBlockConfig = z.infer<typeof funnelSchema>;

export const parseFunnelBlockConfig = (raw: string): FunnelBlockConfig | null => parseFence(raw, funnelSchema);

// url is kept as a free string here and validated for an http(s) scheme at render
// time, so a malformed link degrades to no link rather than voiding the whole quote.
const quoteSchema = z.object({
  text: z.string(),
  source: z.string(),
  date: z.string().optional(),
  url: z.string().optional(),
  verified: z.boolean().optional(),
  note: z.string().optional(),
});

export type QuoteBlockConfig = z.infer<typeof quoteSchema>;

export const parseQuoteBlockConfig = (raw: string): QuoteBlockConfig | null => parseFence(raw, quoteSchema);

const compareItemSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const compareColumnSchema = z.object({
  title: z.string(),
  tone: toneSchema.optional(),
  items: z.array(compareItemSchema).min(1),
});

// at least two columns: a single column is a stat/record, not a comparison.
const compareSchema = z.object({
  title: z.string().optional(),
  columns: z.array(compareColumnSchema).min(2),
  note: z.string().optional(),
});

export type CompareItem = z.infer<typeof compareItemSchema>;
export type CompareColumn = z.infer<typeof compareColumnSchema>;
export type CompareBlockConfig = z.infer<typeof compareSchema>;

export const parseCompareBlockConfig = (raw: string): CompareBlockConfig | null => parseFence(raw, compareSchema);

const clusterEdgeSchema = z
  .object({
    source: z.string().trim().min(1),
    target: z.string().trim().min(1),
    attribute: z.string().trim().min(1),
    value: z.string().optional(),
    detail: z.string().optional(),
    evidence: z.array(z.string().trim().min(1)).min(1),
  })
  // A connection describes a relationship between two distinct entities.
  .refine(edge => edge.source !== edge.target, 'a connection must join two distinct entities');

const clusterSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  edges: z.array(clusterEdgeSchema).min(1),
  note: z.string().optional(),
});

export type ClusterEdge = z.infer<typeof clusterEdgeSchema>;
export type ClusterBlockConfig = z.infer<typeof clusterSchema>;

export const parseClusterBlockConfig = (raw: string): ClusterBlockConfig | null => parseFence(raw, clusterSchema);
